/**
 * mesas.types.ts — Contratos del módulo Mesas (servicio a mesa + espacio).
 *
 * QUÉ HACE: Define Mesa, Piso, Zona, ElementoPlano, OrdenMesa, sus ítems,
 *   comandas y estados.
 * QUÉ EXPORTA: Mesa, MesaInput, MesaForma, Piso, PisoInput, Zona, ZonaInput,
 *   ElementoPlano, ElementoPlanoInput, ElementoPlanoTipo, CapaPlano, PlanoData,
 *   GuardarPlanoInput, OrdenMesa, OrdenMesaItem, ComandaMesa, EstadoOrdenMesa,
 *   EstadoMesa, MesasServiceError.
 * QUÉ NO DEBE HACER: No conoce React, red ni persistencia.
 */
import type { CartItem } from "@/types";

/** Forma de una mesa en el plano visual. */
export type MesaForma =
  | "cuadrada"
  | "redonda"
  | "rectangular"
  | "ovalada"
  | "booth";

/** Distribución de las sillas respecto a la mesa. */
export type DisposicionSillas =
  | "alrededor"
  | "frontal"
  | "lateral"
  | "banqueta"
  | "personalizada";

/** Silla de una mesa (coordenadas locales a la mesa, en px). */
export interface SillaMesa {
  id: string;
  x: number;
  y: number;
  /** Grados; el respaldo mira hacia afuera. */
  angulo: number;
}

/** Mesa física del salón (catálogo del negocio). */
export interface Mesa {
  id: string;
  businessId: string;
  /** Número o nombre visible: "1", "Terraza 2"… */
  numero: string;
  /** Zona denormalizada (compatibilidad con órdenes y demo). */
  zona?: string;
  /** Piso y zona reales del espacio (fuente de verdad). */
  pisoId?: string;
  zonaId?: string;
  activa: boolean;
  /** Posición en el plano (px, relativa al piso). */
  posX?: number;
  posY?: number;
  /** Forma dibujada en el plano. */
  forma?: MesaForma;
  /** Tamaño en px dentro del plano. */
  ancho?: number;
  alto?: number;
  /** Rotación en grados (0-359). */
  rotacion?: number;
  /** Comensales que caben en la mesa. */
  capacidad?: number;
  /** Distribución de sillas (por defecto "alrededor"). */
  disposicionSillas?: DisposicionSillas;
  /** Sillas personalizadas (solo si disposicionSillas = "personalizada"). */
  sillas?: SillaMesa[];
  createdAt: string;
  updatedAt: string;
}

/** Datos editables de una mesa (crear/editar). */
export interface MesaInput {
  numero: string;
  zona?: string;
  pisoId?: string;
  zonaId?: string;
  activa?: boolean;
  posX?: number;
  posY?: number;
  forma?: MesaForma;
  ancho?: number;
  alto?: number;
  rotacion?: number;
  capacidad?: number;
  disposicionSillas?: DisposicionSillas;
  sillas?: SillaMesa[];
}

/** Piso/nivel del negocio: "Piso 1", "Piso 2", "Terraza", "Sótano". */
export interface Piso {
  id: string;
  businessId: string;
  nombre: string;
  /** Orden de las pestañas. */
  orden: number;
  /** Tamaño lógico del lienzo en px. */
  ancho: number;
  alto: number;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Datos editables de un piso. */
export interface PisoInput {
  nombre: string;
  orden?: number;
  ancho?: number;
  alto?: number;
  activo?: boolean;
}

/** Zona/área dentro de un piso: "Salón", "Terraza", "Barra", "Balcón". */
export interface Zona {
  id: string;
  businessId: string;
  pisoId: string;
  nombre: string;
  /** Color hex para leyenda y fondo suave en el plano. */
  color?: string;
  orden: number;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Datos editables de una zona. */
export interface ZonaInput {
  pisoId: string;
  nombre: string;
  color?: string;
  orden?: number;
  activo?: boolean;
}

/** Tipos de elemento estructural/decorativo del plano. */
export type ElementoPlanoTipo =
  | "pared"
  | "puerta"
  | "ventana"
  | "columna"
  | "escalera"
  | "baranda"
  | "barra"
  | "barra_curva"
  | "mostrador"
  | "vitrina"
  | "estanteria"
  | "cocina"
  | "bano"
  | "caja"
  | "basura"
  | "planta"
  | "alfombra"
  | "zona"
  | "texto";

/** Capa lógica del editor (visibilidad y bloqueo por capa). */
export type CapaPlano = "estructura" | "mobiliario" | "decoracion";

/** Elemento no-mesa del plano (paredes, puertas, mobiliario fijo…). */
export interface ElementoPlano {
  id: string;
  businessId: string;
  pisoId: string;
  tipo: ElementoPlanoTipo;
  capa: CapaPlano;
  /** Posición y tamaño en px, relativa al piso. */
  x: number;
  y: number;
  ancho: number;
  alto: number;
  /** Rotación en grados (0-359). */
  rotacion: number;
  color?: string;
  etiqueta?: string;
  /** true = obstáculo físico (futuro cálculo de rutas). */
  bloqueaPaso?: boolean;
  /** true = no se puede mover/editar en el editor. */
  bloqueado?: boolean;
  /** Orden de apilado (mayor = encima). */
  ordenZ?: number;
  createdAt: string;
  updatedAt: string;
}

/** Datos editables de un elemento del plano (el id se genera en cliente). */
export interface ElementoPlanoInput {
  id: string;
  tipo: ElementoPlanoTipo;
  capa?: CapaPlano;
  x: number;
  y: number;
  ancho: number;
  alto: number;
  rotacion?: number;
  color?: string;
  etiqueta?: string;
  bloqueaPaso?: boolean;
  bloqueado?: boolean;
  ordenZ?: number;
}

/** Espacio completo de un piso (lo que dibuja el plano). */
export interface PlanoData {
  piso: Piso;
  zonas: Zona[];
  mesas: Mesa[];
  elementos: ElementoPlano[];
}

/**
 * Guardado atómico del plano: el payload es el estado autoritativo del piso
 * (se eliminan las mesas/elementos que no vengan en la lista).
 */
export interface GuardarPlanoInput {
  pisoId: string;
  piso?: PisoInput;
  mesas: (MesaInput & { id: string })[];
  elementos: ElementoPlanoInput[];
}

/**
 * Ciclo de vida de una orden de mesa:
 * abierta → enviada → preparando → lista → servida → por_cobrar → cobrada
 * (anulada desde cualquier estado activo).
 */
export type EstadoOrdenMesa =
  | "abierta"
  | "enviada"
  | "preparando"
  | "lista"
  | "servida"
  | "por_cobrar"
  | "cobrada"
  | "anulada";

/** Estado visual derivado de una mesa según su orden activa. */
export type EstadoMesa = "libre" | "ocupada" | "por_cobrar" | "en_espera";

/** Línea de una orden: snapshot del carrito + si ya fue enviada a cocina. */
export interface OrdenMesaItem {
  cartItem: CartItem;
  /** true cuando la línea ya se imprimió/envió en una comanda. */
  enviado: boolean;
  comandaId?: string;
}

/** Comanda enviada a cocina (agrupa los ítems enviados en ese momento). */
export interface ComandaMesa {
  id: string;
  /** Consecutivo por orden: 1, 2, 3… */
  numero: number;
  enviadaEn: string;
  itemIds: string[];
  /** true si se reimprimió al menos una vez. */
  reimpresa?: boolean;
}

/** Orden de mesa (no es una venta: la venta nace al cobrar en Caja). */
export interface OrdenMesa {
  id: string;
  businessId: string;
  /** Consecutivo por negocio. */
  numero: number;
  /**
   * Tipo de atención: `"mesa"` (default, órdenes con mesa) o `"llevar"`
   * (mostrador, sin mesa). En órdenes viejas se asume `"mesa"`.
   */
  tipoEntrega?: "mesa" | "llevar";
  /** Mesa de la orden. Vacío/undefined cuando es "Para llevar". */
  mesaId?: string;
  mesaNumero?: string;
  zona?: string;
  meseroId: string;
  meseroNombre: string;
  items: OrdenMesaItem[];
  estado: EstadoOrdenMesa;
  comandas: ComandaMesa[];
  /** Suma estimada de la orden (sin descuentos ni propina). */
  total: number;
  createdAt: string;
  updatedAt: string;
  motivoAnulacion?: string;
  /** Venta de Caja asociada cuando la orden fue cobrada. */
  ventaId?: string;
  cajaId?: string;
  /** Marcas de tiempo por transición (para medir tiempos de cocina/servicio). */
  preparandoEn?: string;
  listaEn?: string;
  servidaEn?: string;
  cobradaEn?: string;
}

/** Entrada para crear una orden con su primer borrador de ítems. */
export interface CrearOrdenMesaInput {
  /** Mesa (obligatoria salvo cuando `tipoEntrega === "llevar"`). */
  mesaId?: string;
  /** `"llevar"` para órdenes de mostrador sin mesa. */
  tipoEntrega?: "mesa" | "llevar";
  meseroId: string;
  meseroNombre: string;
  items: CartItem[];
}

/** Error tipado del servicio de mesas (código estable para la UI). */
export class MesasServiceError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = "MesasServiceError";
    this.code = code;
  }
}
